import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  FaBroadcastTower,
  FaMicrophone,
  FaStop,
  FaVolumeUp,
  FaVolumeMute,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";

import {
  fetchRadioGroups,
  fetchRadioMessages,
  uploadRadioMessage,
  normalizeRadioAudioUrl,
  fetchRadioAudioBlob,
  radioAudioMimeType,
  isWebmRadioUrl,
  convertBlobToWav,
  encodeWavFromAudioBuffer,
} from "./radioApi";

import "./RadioLivePanel.css";

function newUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "radio-" + Date.now() + "-" + Math.random().toString(16).slice(2);
}

function formatTime(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return hh + ":" + mm;
  } catch (e) {
    return "";
  }
}

function messagesSameList(prev, next) {
  if (!prev || !next || prev.length !== next.length) return false;
  for (var i = 0; i < prev.length; i++) {
    if (prev[i].uuid !== next[i].uuid) return false;
    if (prev[i].audioUrl !== next[i].audioUrl) return false;
  }
  return true;
}

function pickRecordingMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/aac",
  ];
  for (var i = 0; i < candidates.length; i++) {
    if (MediaRecorder.isTypeSupported(candidates[i])) {
      return candidates[i];
    }
  }
  return "audio/webm";
}

function recordingFileExtension(mimeType) {
  const lower = (mimeType || "").toLowerCase();
  if (lower.includes("mp4") || lower.includes("aac")) {
    return ".m4a";
  }
  return ".webm";
}

/**
 * Escuta ao vivo + gravação pelo navegador (somente áudio, sem transcrição).
 * Usado na aba Áudios do Rádio e no overlay do Painel.
 */
class RadioLivePanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      groups: [],
      messages: [],
      channelValue: "all",
      sendChannelId: null,
      liveOn: false,
      recording: false,
      uploading: false,
      loading: true,
      statusText: "",
      playingUuid: null,
      playedTick: 0,
      audioSrcTick: 0,
    };
    this.pollTimer = null;
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.recordStream = null;
    this.recordAudioContext = null;
    this.recordProcessor = null;
    this.recordSource = null;
    this.recordPcmChunks = [];
    this.recordingActive = false;
    this.recordingStartedAt = 0;
    this.uploadQueue = Promise.resolve();
    this.warmStream = null;
    this.warmAudioContext = null;
    this.micWarmPromise = null;
    this.audioEl = null;
    this.audioRefs = {};
    this.autoPlaying = false;
    this.programmaticPlay = false;
    this.programmaticPause = false;
    this.sequentialActive = false;
    this.queueTotal = 0;
    this.queueIndex = 0;
    this.playQueue = [];
    this.isPlaying = false;
    this.fallbackAudioEl = null;
    this.hiddenVideoEl = null;
    this.seenUuids = {};
    this.lastPollIso = null;
    this.playedUuids = {};
    this.sentByWebUuids = {};
    this.listRefreshTimer = null;
    this.loadMessagesGeneration = 0;
    this.audioBlobUrls = {};
    this.audioBlobLoading = {};
  }

  revokeAudioBlobUrls = (exceptUuid) => {
    const self = this;
    Object.keys(this.audioBlobUrls).forEach(function (uuid) {
      if (exceptUuid && uuid === exceptUuid) return;
      try {
        URL.revokeObjectURL(self.audioBlobUrls[uuid]);
      } catch (e) {}
      delete self.audioBlobUrls[uuid];
      delete self.audioBlobLoading[uuid];
    });
  };

  ensureAudioBlobSrc = async (message) => {
    if (!message || !message.uuid || !isWebmRadioUrl(message.audioUrl)) {
      return null;
    }
    if (this.audioBlobUrls[message.uuid]) {
      return this.audioBlobUrls[message.uuid];
    }
    if (this.audioBlobLoading[message.uuid]) {
      return this.audioBlobLoading[message.uuid];
    }

    const self = this;
    const promise = fetchRadioAudioBlob(message.audioUrl)
      .then(function (blob) {
        return convertBlobToWav(blob).catch(function () {
          return new Blob([blob], { type: "audio/webm" });
        });
      })
      .then(function (playbackBlob) {
        const url = URL.createObjectURL(playbackBlob);
        self.audioBlobUrls[message.uuid] = url;
        delete self.audioBlobLoading[message.uuid];
        self.setState(function (prev) {
          return { audioSrcTick: prev.audioSrcTick + 1 };
        });
        return url;
      })
      .catch(function (error) {
        delete self.audioBlobLoading[message.uuid];
        console.warn("radio webm blob", error);
        return null;
      });
    this.audioBlobLoading[message.uuid] = promise;
    return promise;
  };

  prepareWebmPlayback = (messages) => {
    const active = {};
    const self = this;
    (messages || []).forEach(function (m) {
      if (m.uuid) active[m.uuid] = true;
      if (isWebmRadioUrl(m.audioUrl)) {
        self.ensureAudioBlobSrc(m);
      }
    });
    Object.keys(this.audioBlobUrls).forEach(function (uuid) {
      if (!active[uuid]) {
        try {
          URL.revokeObjectURL(self.audioBlobUrls[uuid]);
        } catch (e) {}
        delete self.audioBlobUrls[uuid];
        delete self.audioBlobLoading[uuid];
      }
    });
  };

  getPlaybackSrc = (message) => {
    if (!message || !message.audioUrl) return null;
    if (message.uuid && this.audioBlobUrls[message.uuid]) {
      return this.audioBlobUrls[message.uuid];
    }
    return normalizeRadioAudioUrl(message.audioUrl);
  };

  findIncomingMessages = (results, prevMessages) => {
    if (!prevMessages || !prevMessages.length) return [];
    const prevUuids = {};
    prevMessages.forEach((m) => {
      if (m.uuid) prevUuids[m.uuid] = true;
    });
    return results.filter(
      (m) =>
        m.uuid &&
        !prevUuids[m.uuid] &&
        this.shouldAutoPlayMessage(m)
    );
  };

  enqueueIncomingAutoplay = (incoming) => {
    if (!incoming || !incoming.length || !this.state.liveOn) return;
    incoming
      .slice()
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
      .forEach((m) => this.enqueuePlay(m));
  };

  isReceivedMessage = (message) => {
    if (!message || !message.uuid) return false;
    if (message.senderUserId) return false;
    if (this.sentByWebUuids[message.uuid]) return false;
    return true;
  };

  shouldAutoPlayMessage = (message) => {
    if (!this.isReceivedMessage(message)) return false;
    if (!normalizeRadioAudioUrl(message.audioUrl)) return false;
    if (this.playedUuids[message.uuid]) return false;
    return true;
  };

  registerOwnSentMessage = (uuid) => {
    if (!uuid) return;
    this.sentByWebUuids[uuid] = true;
    this.seenUuids[uuid] = true;
  };

  getPlayedStorageKey = () => {
    return "radio-played-" + (this.props.farmId || "");
  };

  getLiveStorageKey = () => {
    return "radio-live-on-" + (this.props.farmId || "");
  };

  loadLivePref = () => {
    try {
      const raw = localStorage.getItem(this.getLiveStorageKey());
      if (raw === null) return true;
      return raw === "1" || raw === "true";
    } catch (e) {
      return true;
    }
  };

  saveLivePref = (liveOn) => {
    try {
      localStorage.setItem(this.getLiveStorageKey(), liveOn ? "1" : "0");
    } catch (e) {}
  };

  loadPlayedUuids = () => {
    try {
      const raw = localStorage.getItem(this.getPlayedStorageKey());
      if (raw) {
        this.playedUuids = JSON.parse(raw) || {};
      }
    } catch (e) {
      this.playedUuids = {};
    }
  };

  savePlayedUuids = () => {
    try {
      localStorage.setItem(
        this.getPlayedStorageKey(),
        JSON.stringify(this.playedUuids)
      );
    } catch (e) {}
  };

  markPlayed = (uuid) => {
    if (!uuid || this.playedUuids[uuid]) return;
    this.playedUuids[uuid] = true;
    this.savePlayedUuids();
    this.setState(function (prev) {
      return { playedTick: prev.playedTick + 1 };
    }, this.notifyUnplayedCount);
  };

  countBadgeUnplayed = () => {
    const since = this.props.unplayedBadgeSince;
    if (!since) return 0;
    const sinceMs = new Date(since).getTime();
    return (this.state.messages || []).filter((m) => {
      if (!this.isReceivedMessage(m)) return false;
      if (this.playedUuids[m.uuid]) return false;
      if (!m.timestamp) return false;
      return new Date(m.timestamp).getTime() > sinceMs;
    }).length;
  };

  notifyUnplayedCount = () => {
    const cb = this.props.onUnplayedCountChange;
    if (!cb) return;
    cb(this.countBadgeUnplayed());
  };

  componentDidMount() {
    this.loadPlayedUuids();
    const liveOn = this.loadLivePref();
    this.setState({ liveOn: liveOn }, () => {
      this.bootstrap().then(() => {
        if (liveOn) this.startLive(false);
      });
    });
    this.listRefreshTimer = setInterval(() => {
      this.loadMessages({ silent: true });
    }, 5000);
    this.preflightMicrophone();
  }

  preflightMicrophone = () => {
    const self = this;
    if (!navigator.permissions || !navigator.permissions.query) {
      return;
    }
    navigator.permissions
      .query({ name: "microphone" })
      .then(function (status) {
        if (status.state === "granted") {
          self.warmMicrophone();
        }
      })
      .catch(function () {});
  };

  componentDidUpdate(prevProps) {
    if (prevProps.unplayedBadgeSince !== this.props.unplayedBadgeSince) {
      this.notifyUnplayedCount();
    }
  }

  componentWillUnmount() {
    if (this.listRefreshTimer) {
      clearInterval(this.listRefreshTimer);
      this.listRefreshTimer = null;
    }
    this.stopLive(true);
    this.cancelRecording();
    this.releaseWarmMicrophone();
    this.revokeAudioBlobUrls();
    if (this.hiddenVideoEl) {
      try {
        this.hiddenVideoEl.pause();
        this.hiddenVideoEl.remove();
      } catch (e) {}
      this.hiddenVideoEl = null;
    }
    if (this.audioEl) {
      try {
        this.audioEl.pause();
      } catch (e) {}
      this.audioEl = null;
    }
  }

  bootstrap = async () => {
    const { farmId, t } = this.props;
    this.setState({ loading: true });
    try {
      const data = await fetchRadioGroups(farmId);
      const groups = data.results || [];
      const sendChannelId = groups.length ? groups[0].id : null;
      await new Promise((resolve) => {
        this.setState(
          {
            groups: groups,
            sendChannelId: sendChannelId,
            loading: false,
          },
          resolve
        );
      });
      await this.loadMessages();
    } catch (error) {
      console.error(error);
      toast.error(t("Falha ao carregar canais do rádio"));
      this.setState({ loading: false });
    }
  };

  loadMessages = async (options) => {
    const { farmId, t } = this.props;
    const { channelValue, messages: prevMessages, liveOn } = this.state;
    const generation = ++this.loadMessagesGeneration;
    try {
      const params = { limit: 80 };
      if (channelValue && channelValue !== "all") {
        params.group_id = channelValue;
      }
      const data = await fetchRadioMessages(farmId, params);
      if (generation !== this.loadMessagesGeneration) return;

      const results = data.results || [];
      const incoming = liveOn
        ? this.findIncomingMessages(results, prevMessages)
        : [];

      results.forEach((m) => {
        if (m.uuid) this.seenUuids[m.uuid] = true;
      });
      if (results.length) {
        this.lastPollIso = results[0].timestamp;
      }

      const listChanged = !messagesSameList(prevMessages, results);
      await new Promise((resolve) => {
        this.setState(
          function (prev) {
            if (!listChanged) {
              return { loading: false };
            }
            return { messages: results, loading: false };
          },
          () => {
            this.notifyUnplayedCount();
            resolve();
          }
        );
      });
      if (generation !== this.loadMessagesGeneration) return;

      this.prepareWebmPlayback(results);

      if (incoming.length) {
        window.requestAnimationFrame(() => {
          this.enqueueIncomingAutoplay(incoming);
        });
      }
      this.notifyUnplayedCount();
    } catch (error) {
      console.error(error);
      toast.error(t("Falha ao carregar áudios"));
    }
  };

  startLive = (savePref) => {
    const { t } = this.props;
    if (savePref !== false) {
      this.saveLivePref(true);
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
    this.pollTimer = setInterval(() => {
      this.pollNewMessages();
    }, 4000);
    this.setState({ liveOn: true, statusText: t("Reprodução automática ligada") });
    this.loadMessages({ silent: true });
  };

  stopLive = (silent) => {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.playQueue = [];
    this.isPlaying = false;
    this.stopAllAudio();
    if (this.audioEl) {
      try {
        this.audioEl.pause();
      } catch (e) {}
    }
    if (!silent) {
      this.saveLivePref(false);
      this.setState({
        liveOn: false,
        statusText: this.props.t("Reprodução automática desligada"),
      });
    } else {
      this.setState({ liveOn: false });
    }
  };

  toggleLive = () => {
    if (this.state.liveOn) this.stopLive(false);
    else this.startLive();
  };

  pollNewMessages = async () => {
    if (!this.state.liveOn) return;
    const { farmId } = this.props;
    const { channelValue } = this.state;
    try {
      const params = { limit: 30 };
      if (channelValue && channelValue !== "all") {
        params.group_id = channelValue;
      }
      if (this.lastPollIso) {
        params.since = this.lastPollIso;
      }
      const data = await fetchRadioMessages(farmId, params);
      const results = (data.results || []).slice().reverse();
      const fresh = [];
      results.forEach((m) => {
        if (
          m.uuid &&
          !this.seenUuids[m.uuid] &&
          this.shouldAutoPlayMessage(m)
        ) {
          this.seenUuids[m.uuid] = true;
          fresh.push(m);
        }
      });
      if (fresh.length) {
        this.lastPollIso = fresh[fresh.length - 1].timestamp;
        this.setState(
          (prev) => ({
            messages: fresh
              .slice()
              .reverse()
              .concat(prev.messages)
              .slice(0, 80),
            statusText:
              this.props.t("Novo áudio") +
              ": " +
              (fresh[fresh.length - 1].senderName || ""),
          }),
          () => {
            window.requestAnimationFrame(() => {
              this.enqueueIncomingAutoplay(fresh);
            });
          }
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  isNearEnd = (el) => {
    if (!el || !el.duration || !isFinite(el.duration)) return false;
    return el.currentTime >= el.duration - 0.35;
  };

  pauseOtherAudio = (exceptUuid) => {
    this.programmaticPause = true;
    if (this.fallbackAudioEl) {
      try {
        this.fallbackAudioEl.pause();
      } catch (e) {}
      this.fallbackAudioEl = null;
    }
    const playingUuid = this.state.playingUuid;
    if (playingUuid && playingUuid !== exceptUuid) {
      const current = this.audioRefs[playingUuid];
      if (current) {
        try {
          current.pause();
        } catch (e) {}
      }
    }
    this.programmaticPause = false;
  };

  stopAllAudio = () => {
    this.programmaticPause = true;
    if (this.fallbackAudioEl) {
      try {
        this.fallbackAudioEl.pause();
      } catch (e) {}
      this.fallbackAudioEl = null;
    }
    if (this.state.playingUuid) {
      const el = this.audioRefs[this.state.playingUuid];
      if (el) {
        try {
          el.pause();
        } catch (e) {}
      }
    }
    this.programmaticPause = false;
  };

  buildSequentialQueue = (startMessage) => {
    if (!startMessage || !startMessage.uuid) return [];

    const chronological = this.state.messages
      .filter((m) => m.uuid && normalizeRadioAudioUrl(m.audioUrl))
      .slice()
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

    const unplayed = chronological.filter((m) => !this.playedUuids[m.uuid]);
    const startIndex = unplayed.findIndex((m) => m.uuid === startMessage.uuid);
    if (startIndex < 0) {
      return normalizeRadioAudioUrl(startMessage.audioUrl) ? [startMessage] : [];
    }
    return unplayed.slice(startIndex);
  };

  getQueueStatusText = () => {
    const { t } = this.props;
    if (this.sequentialActive && this.queueTotal > 1) {
      return (
        t("Reproduzindo fila") +
        " (" +
        this.queueIndex +
        "/" +
        this.queueTotal +
        ")"
      );
    }
    return "";
  };

  stopSequentialPlayback = (clearStatus) => {
    this.sequentialActive = false;
    this.queueTotal = 0;
    this.queueIndex = 0;
    this.autoPlaying = false;
    this.isPlaying = false;
    this.playQueue = [];
    this.stopAllAudio();
    if (clearStatus) {
      this.setState({ playingUuid: null, statusText: "" });
    } else {
      this.setState({ playingUuid: null });
    }
  };

  beginSequentialFromUser = (startMessage) => {
    const queue = this.buildSequentialQueue(startMessage);
    if (!queue.length) return;

    this.pauseOtherAudio(startMessage.uuid);

    this.sequentialActive = queue.length > 1;
    this.queueTotal = queue.length;
    this.queueIndex = 1;
    this.autoPlaying = true;
    this.isPlaying = true;
    this.playQueue = queue.slice(1);

    const { t } = this.props;
    this.setState({
      playingUuid: startMessage.uuid,
      statusText:
        queue.length > 1
          ? t("Reproduzindo fila") + " (1/" + queue.length + ")"
          : t("Reproduzindo") + ": " + (startMessage.senderName || ""),
    });
  };

  onListAudioEnded = (message) => {
    this.isPlaying = false;
    if (message && message.uuid) {
      this.markPlayed(message.uuid);
    }

    if (this.playQueue.length && this.autoPlaying) {
      this.drainQueue();
      return;
    }

    this.sequentialActive = false;
    this.queueTotal = 0;
    this.queueIndex = 0;
    this.autoPlaying = false;
    this.setState({ playingUuid: null, statusText: "" });
  };

  enqueuePlay = (message) => {
    if (!message || !normalizeRadioAudioUrl(message.audioUrl)) return;
    if (this.playQueue.some((m) => m.uuid === message.uuid)) return;
    if (this.state.playingUuid === message.uuid) return;
    this.playQueue.push(message);
    if (!this.autoPlaying && this.state.liveOn) {
      this.autoPlaying = true;
    }
    this.drainQueue();
  };

  drainQueue = () => {
    if (this.isPlaying || !this.playQueue.length) return;
    this.isPlaying = true;
    const next = this.playQueue.shift();
    if (this.sequentialActive) {
      this.queueIndex += 1;
    }
    this.playMessage(next);
  };

  playMessage = (message) => {
    const self = this;
    const src = this.getPlaybackSrc(message);
    if (!message || !src) {
      this.isPlaying = false;
      this.drainQueue();
      return;
    }

    this.programmaticPause = true;
    this.pauseOtherAudio(message.uuid);
    this.programmaticPause = false;

    const listEl = message.uuid ? this.audioRefs[message.uuid] : null;
    if (listEl) {
      this.playMessageOnElement(message, listEl);
      return;
    }

    window.requestAnimationFrame(function () {
      const retryEl = message.uuid ? self.audioRefs[message.uuid] : null;
      if (retryEl) {
        self.playMessageOnElement(message, retryEl);
        return;
      }
      window.requestAnimationFrame(function () {
        const retryEl2 = message.uuid ? self.audioRefs[message.uuid] : null;
        if (retryEl2) {
          self.playMessageOnElement(message, retryEl2);
        } else {
          self.playMessageFallback(message, src);
        }
      });
    });
  };

  playMessageOnElement = async (message, listEl) => {
    const self = this;
    if (isWebmRadioUrl(message.audioUrl)) {
      const blobSrc = await this.ensureAudioBlobSrc(message);
      if (blobSrc && listEl.src !== blobSrc) {
        listEl.src = blobSrc;
        try {
          listEl.load();
        } catch (e) {}
      }
    }
    this.audioEl = listEl;
    const statusText =
      this.getQueueStatusText() ||
      this.props.t("Reproduzindo") + ": " + (message.senderName || "");
    this.setState({
      playingUuid: message.uuid,
      statusText: statusText,
    });
    try {
      if (listEl.currentTime > 0.05) {
        listEl.currentTime = 0;
      }
    } catch (e) {}
    this.programmaticPlay = true;
    const playPromise = listEl.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch(function () {
        self.programmaticPlay = false;
        self.isPlaying = false;
        if (isWebmRadioUrl(message.audioUrl)) {
          const raw = normalizeRadioAudioUrl(message.audioUrl);
          self.playViaHiddenVideo(raw, message, function (ok) {
            if (ok) {
              self.onListAudioEnded(message);
              return;
            }
            toast.warn(
              self.props.t("Não foi possível reproduzir este áudio, pulando.")
            );
            self.drainQueue();
          });
          return;
        }
        toast.warn(
          self.props.t("Não foi possível reproduzir este áudio, pulando.")
        );
        self.drainQueue();
      });
    }
  };

  skipFailedAndContinue = (message) => {
    this.isPlaying = false;
    if (message && message.uuid) {
      this.markPlayed(message.uuid);
    }
    if (this.playQueue.length && this.autoPlaying) {
      this.drainQueue();
      return;
    }
    this.setState({ playingUuid: null });
  };

  playViaHiddenVideo = (src, message, onDone) => {
    const self = this;
    if (this.hiddenVideoEl) {
      try {
        this.hiddenVideoEl.pause();
        this.hiddenVideoEl.remove();
      } catch (e) {}
      this.hiddenVideoEl = null;
    }
    const video = document.createElement("video");
    video.style.cssText =
      "position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none";
    video.volume = 1;
    video.playsInline = true;
    video.src = src;
    this.hiddenVideoEl = video;
    document.body.appendChild(video);
    video.onended = function () {
      video.remove();
      if (self.hiddenVideoEl === video) self.hiddenVideoEl = null;
      if (message && message.uuid) self.markPlayed(message.uuid);
      onDone(true);
    };
    video.onerror = function () {
      video.remove();
      if (self.hiddenVideoEl === video) self.hiddenVideoEl = null;
      onDone(false);
    };
    const playPromise = video.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch(function () {
        onDone(false);
      });
    }
  };

  playMessageFallback = async (message, src) => {
    const self = this;
    if (this.audioEl && this.audioEl !== this.audioRefs[message.uuid]) {
      try {
        this.audioEl.pause();
      } catch (e) {}
    }

    let playbackSrc = src;
    if (isWebmRadioUrl(message.audioUrl)) {
      const blobSrc = await this.ensureAudioBlobSrc(message);
      if (blobSrc) playbackSrc = blobSrc;
    }

    const el = new Audio(playbackSrc);
    el.volume = 1;
    this.audioEl = el;
    this.fallbackAudioEl = el;

    el.onended = function () {
      self.fallbackAudioEl = null;
      self.setState({ playingUuid: null });
      self.onListAudioEnded(message);
    };
    el.onerror = function () {
      self.fallbackAudioEl = null;
      if (isWebmRadioUrl(message.audioUrl)) {
        self.playViaHiddenVideo(playbackSrc, message, function (ok) {
          if (ok) {
            self.setState({ playingUuid: null, statusText: "" });
            self.onListAudioEnded(message);
            return;
          }
          self.isPlaying = false;
          self.setState({ playingUuid: null });
          toast.warn(self.props.t("Não foi possível reproduzir este áudio."));
          if (self.playQueue.length) {
            self.drainQueue();
          } else {
            self.stopSequentialPlayback(true);
          }
        });
        return;
      }
      self.isPlaying = false;
      self.setState({ playingUuid: null });
      toast.warn(self.props.t("Não foi possível reproduzir este áudio."));
      if (self.playQueue.length) {
        self.drainQueue();
      } else {
        self.stopSequentialPlayback(true);
      }
    };

    const statusText =
      this.getQueueStatusText() ||
      this.props.t("Reproduzindo") + ": " + (message.senderName || "");
    this.setState({
      playingUuid: message.uuid,
      statusText: statusText,
    });

    const playPromise = el.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch(function () {
        self.fallbackAudioEl = null;
        if (isWebmRadioUrl(message.audioUrl)) {
          self.playViaHiddenVideo(playbackSrc, message, function (ok) {
            if (ok) {
              self.onListAudioEnded(message);
              return;
            }
            self.isPlaying = false;
            toast.warn(
              self.props.t(
                "Permita o áudio do navegador (clique na página) para escuta automática."
              )
            );
            self.skipFailedAndContinue(message);
          });
          return;
        }
        self.isPlaying = false;
        toast.warn(
          self.props.t(
            "Permita o áudio do navegador (clique na página) para escuta automática."
          )
        );
        self.skipFailedAndContinue(message);
      });
    }
  };

  onChannelChange = (event) => {
    const value = event.target.value;
    this.setState({ channelValue: value }, () => {
      this.loadMessages();
    });
  };

  onSendChannelChange = (event) => {
    const value = event.target.value;
    this.setState({
      sendChannelId: value ? Number(value) : null,
    });
  };

  resolveSendGroupId = () => {
    const { channelValue, sendChannelId, groups } = this.state;
    if (channelValue && channelValue !== "all") {
      return Number(channelValue);
    }
    if (sendChannelId) return Number(sendChannelId);
    if (groups.length) return groups[0].id;
    return null;
  };

  getAudioConstraints = () => {
    return {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    };
  };

  isStreamLive = (stream) => {
    if (!stream || !stream.active) return false;
    const tracks = stream.getAudioTracks();
    return (
      tracks.length > 0 &&
      tracks[0].readyState === "live" &&
      tracks[0].enabled
    );
  };

  warmMicrophone = () => {
    if (this.micWarmPromise) return this.micWarmPromise;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return Promise.resolve(false);
    }
    if (this.isStreamLive(this.warmStream)) {
      return Promise.resolve(true);
    }

    const self = this;
    this.micWarmPromise = navigator.mediaDevices
      .getUserMedia(this.getAudioConstraints())
      .then(async function (stream) {
        self.warmStream = stream;
        const ctx = await self.ensureRecordingAudioContext();
        if (ctx) {
          self.setState(function (prev) {
            if (prev.recording || prev.uploading) return null;
            return { statusText: self.props.t("Microfone pronto") };
          });
        }
        return true;
      })
      .catch(function (error) {
        console.warn("radio mic warm", error);
        return false;
      })
      .finally(function () {
        self.micWarmPromise = null;
      });
    return this.micWarmPromise;
  };

  releaseWarmMicrophone = () => {
    this.micWarmPromise = null;
    if (this.warmStream) {
      try {
        this.warmStream.getTracks().forEach(function (track) {
          track.stop();
        });
      } catch (e) {}
      this.warmStream = null;
    }
    if (this.warmAudioContext) {
      try {
        this.warmAudioContext.close();
      } catch (e) {}
      this.warmAudioContext = null;
    }
  };

  ensureRecordingAudioContext = async () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!this.warmAudioContext || this.warmAudioContext.state === "closed") {
      this.warmAudioContext = new AudioCtx({ latencyHint: "interactive" });
    }
    if (this.warmAudioContext.state === "suspended") {
      await this.warmAudioContext.resume();
    }
    return this.warmAudioContext;
  };

  ensureRecordingStream = async () => {
    if (this.isStreamLive(this.warmStream)) {
      return this.warmStream;
    }
    await this.warmMicrophone();
    if (this.isStreamLive(this.warmStream)) {
      return this.warmStream;
    }
    const stream = await navigator.mediaDevices.getUserMedia(
      this.getAudioConstraints()
    );
    this.warmStream = stream;
    return stream;
  };

  cleanupRecordingHardware = (keepMicWarm) => {
    if (keepMicWarm === undefined) keepMicWarm = true;
    this.recordingActive = false;
    if (this.recordProcessor) {
      try {
        this.recordProcessor.onaudioprocess = null;
        this.recordProcessor.disconnect();
      } catch (e) {}
      this.recordProcessor = null;
    }
    if (this.recordSource) {
      try {
        this.recordSource.disconnect();
      } catch (e) {}
      this.recordSource = null;
    }
    this.recordStream = null;
    this.recordAudioContext = null;
    if (this.mediaRecorder) {
      try {
        this.mediaRecorder.onstop = null;
        this.mediaRecorder.ondataavailable = null;
        if (this.mediaRecorder.state !== "inactive") {
          this.mediaRecorder.stop();
        }
      } catch (e) {}
      this.mediaRecorder = null;
    }
    this.recordPcmChunks = [];
    this.recordedChunks = [];
    if (!keepMicWarm) {
      this.releaseWarmMicrophone();
    }
  };

  buildWavBlobFromPcm = () => {
    const chunks = this.recordPcmChunks || [];
    if (!chunks.length || !this.recordAudioContext) {
      return null;
    }
    let total = 0;
    chunks.forEach(function (chunk) {
      total += chunk.length;
    });
    if (!total) return null;

    const merged = new Float32Array(total);
    let offset = 0;
    chunks.forEach(function (chunk) {
      merged.set(chunk, offset);
      offset += chunk.length;
    });

    const sampleRate = this.recordAudioContext.sampleRate || 44100;
    const audioBuffer = this.recordAudioContext.createBuffer(1, total, sampleRate);
    audioBuffer.copyToChannel(merged, 0);
    return encodeWavFromAudioBuffer(audioBuffer);
  };

  enqueueRecordingUpload = (task) => {
    const self = this;
    this.uploadQueue = this.uploadQueue
      .then(function () {
        return task();
      })
      .catch(function (error) {
        console.error(error);
        toast.error(self.props.t("Falha ao enviar áudio"));
      })
      .finally(function () {
        self.setState({ uploading: false });
      });
  };

  uploadRecordingBlob = async (uploadBlob) => {
    const { farmId, t } = this.props;
    const groupId = this.resolveSendGroupId();
    if (!uploadBlob || !uploadBlob.size || uploadBlob.size < 800) {
      toast.warn(t("Áudio muito curto."));
      return;
    }
    const file = new File([uploadBlob], "radio-" + Date.now() + ".wav", {
      type: "audio/wav",
    });
    const sentUuid = newUuid();
    const created = await uploadRadioMessage(farmId, {
      file: file,
      metadata: {
        uuid: sentUuid,
        groupId: groupId,
        targetType: "group",
        timestamp: new Date().toISOString(),
      },
    });
    const resolvedUuid = created && created.uuid ? created.uuid : sentUuid;
    this.registerOwnSentMessage(resolvedUuid);
    toast.success(t("Áudio enviado ao campo"));
    this.setState({
      statusText: t("Áudio enviado"),
    });
    await this.loadMessages();
  };

  finishRecordingAndUpload = async (mimeType) => {
    const { t } = this.props;
    const blob = new Blob(this.recordedChunks, {
      type: mimeType || "audio/webm",
    });
    this.recordedChunks = [];
    if (!blob.size || blob.size < 800) {
      toast.warn(t("Áudio muito curto."));
      return;
    }
    let uploadBlob = blob;
    const lower = (mimeType || "").toLowerCase();
    if (lower.includes("webm")) {
      try {
        uploadBlob = await convertBlobToWav(blob);
      } catch (convertError) {
        console.warn("radio upload wav convert", convertError);
      }
    } else if (lower.includes("mp4") || lower.includes("aac")) {
      try {
        uploadBlob = await convertBlobToWav(blob);
      } catch (convertError) {
        console.warn("radio upload wav convert", convertError);
      }
    }
    await this.uploadRecordingBlob(uploadBlob);
  };

  finalizeStoppedRecording = (mimeType) => {
    const self = this;
    window.setTimeout(function () {
      const wavBlob = self.buildWavBlobFromPcm();
      self.cleanupRecordingHardware(true);
      self.enqueueRecordingUpload(function () {
        if (wavBlob) {
          return self.uploadRecordingBlob(wavBlob);
        }
        return self.finishRecordingAndUpload(mimeType);
      });
    }, 120);
  };

  startRecording = async () => {
    const { t } = this.props;
    const groupId = this.resolveSendGroupId();
    if (!groupId) {
      toast.warn(t("Crie um canal antes de gravar."));
      return;
    }
    if (this.state.uploading || this.state.recording || this.recordingActive) {
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error(t("Seu navegador não permite gravar áudio."));
      return;
    }

    this.setState({
      recording: true,
      statusText: this.isStreamLive(this.warmStream)
        ? t("Gravando… toque em Parar para enviar")
        : t("Preparando microfone…"),
    });

    try {
      const stream = await this.ensureRecordingStream();
      this.recordStream = stream;
      this.recordPcmChunks = [];
      this.recordedChunks = [];

      const ctx = await this.ensureRecordingAudioContext();
      if (ctx && stream.getAudioTracks().length) {
        this.recordAudioContext = ctx;
        const source = ctx.createMediaStreamSource(stream);
        this.recordSource = source;
        const processor = ctx.createScriptProcessor(2048, 1, 1);
        this.recordProcessor = processor;
        const self = this;
        processor.onaudioprocess = function (event) {
          if (!self.recordingActive) return;
          self.recordPcmChunks.push(
            new Float32Array(event.inputBuffer.getChannelData(0))
          );
        };
        source.connect(processor);
        processor.connect(ctx.destination);
        this.recordingActive = true;
        this.recordingStartedAt = Date.now();
        this.setState({
          recording: true,
          statusText: t("Gravando… toque em Parar para enviar"),
        });
        return;
      }

      this.startRecordingWithMediaRecorder(stream);
    } catch (error) {
      console.error(error);
      this.cleanupRecordingHardware(true);
      this.setState({ recording: false, statusText: "" });
      toast.error(t("Permita o microfone para gravar."));
    }
  };

  startRecordingWithMediaRecorder = (stream) => {
    const { t } = this.props;
    const mimeType = pickRecordingMimeType();
    this.recordedChunks = [];
    const recorder = new MediaRecorder(stream, { mimeType: mimeType });
    this.mediaRecorder = recorder;
    const self = this;
    recorder.ondataavailable = function (event) {
      if (event.data && event.data.size > 0) {
        self.recordedChunks.push(event.data);
      }
    };
    recorder.onstop = function () {
      self.finalizeStoppedRecording(mimeType);
    };
    recorder.start(200);
    this.recordingActive = true;
    this.setState({
      recording: true,
      statusText: t("Gravando… toque em Parar para enviar"),
    });
  };

  cancelRecording = () => {
    this.recordingActive = false;
    this.cleanupRecordingHardware(true);
    this.setState({ recording: false, statusText: "" });
  };

  stopRecording = () => {
    const { t } = this.props;
    if (!this.recordingActive || !this.state.recording) return;
    const elapsed = Date.now() - (this.recordingStartedAt || 0);
    if (elapsed < 400) {
      toast.warn(t("Aguarde um instante antes de parar a gravação."));
      return;
    }

    this.recordingActive = false;
    this.setState({ recording: false, uploading: true, statusText: "" });

    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      try {
        this.mediaRecorder.requestData();
      } catch (e) {}
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        this.cleanupRecordingHardware();
        this.setState({ uploading: false });
      }
      return;
    }

    this.finalizeStoppedRecording(null);
  };

  toggleRecord = () => {
    if (this.state.uploading) return;
    if (this.state.recording) this.stopRecording();
    else this.startRecording();
  };

  render() {
    const { t, farmId, compact, hideHeader } = this.props;
    const {
      groups,
      messages,
      channelValue,
      sendChannelId,
      liveOn,
      recording,
      uploading,
      loading,
      statusText,
      playingUuid,
    } = this.state;
    void this.state.playedTick;
    void this.state.audioSrcTick;

    const title =
      channelValue === "all"
        ? t("Todos os canais")
        : (groups.find((g) => String(g.id) === String(channelValue)) || {})
            .name || t("Áudios");

    return (
      <div
        className={
          "radio-live-panel" + (compact ? " radio-live-panel--compact" : "")
        }
      >
        {hideHeader ? null : (
        <div className="radio-live-header">
          <strong>
            <FaBroadcastTower style={{ marginRight: 6 }} />
            {t("Rádio ao vivo")}
          </strong>
          {compact ? (
            <Link
              className="btn btn-link btn-xs"
              to={"/" + farmId + "/radio/"}
              title={t("Abrir Rádio")}
            >
              <FaExternalLinkAlt />
            </Link>
          ) : null}
        </div>
        )}

        {loading ? (
          <div className="radio-live-status">{t("Carregando…")}</div>
        ) : (
          <React.Fragment>
            <div className="radio-live-controls">
              <select
                className="form-control input-sm"
                value={channelValue}
                onChange={this.onChannelChange}
                disabled={recording}
              >
                <option value="all">{t("Todos os canais")}</option>
                {groups.map((group) => (
                  <option key={group.id} value={String(group.id)}>
                    {group.name}
                    {group.description ? " — " + group.description : ""}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className={
                  "btn btn-sm " + (liveOn ? "btn-success" : "btn-default")
                }
                onClick={this.toggleLive}
                title={t(
                  "Reproduz automaticamente novos áudios recebidos (tablet ou web)"
                )}
              >
                {liveOn ? <FaVolumeUp /> : <FaVolumeMute />}{" "}
                {liveOn ? t("Auto ▶ ON") : t("Auto ▶ OFF")}
              </button>

              <button
                type="button"
                className={
                  "btn btn-sm " + (recording ? "btn-danger" : "btn-primary")
                }
                onClick={this.toggleRecord}
                onMouseEnter={this.warmMicrophone}
                onFocus={this.warmMicrophone}
                disabled={uploading || !groups.length}
                title={t("Gravar e enviar para o campo")}
              >
                {recording ? <FaStop /> : <FaMicrophone />}{" "}
                {uploading
                  ? t("Enviando…")
                  : recording
                  ? t("Parar")
                  : t("Falar")}
              </button>
            </div>

            {channelValue === "all" ? (
              <div className="radio-live-send-row">
                <label>{t("Enviar no canal")}</label>
                <select
                  className="form-control input-sm"
                  value={sendChannelId != null ? String(sendChannelId) : ""}
                  onChange={this.onSendChannelChange}
                  disabled={recording}
                >
                  {groups.map((group) => (
                    <option key={group.id} value={String(group.id)}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {statusText ? (
              <div className="radio-live-status">{statusText}</div>
            ) : null}

            <div className="radio-live-list">
              <div className="radio-live-list-title">
                {title} · {messages.length} {t("áudios")}
              </div>
              {messages.length === 0 ? (
                <div className="radio-live-empty">
                  {t("Nenhum áudio neste filtro ainda.")}
                </div>
              ) : (
                messages.map((message) => {
                  const isUnplayed =
                    message.uuid && !this.playedUuids[message.uuid];
                  const isFromWeb = !this.isReceivedMessage(message);
                  const isPlayingNow =
                    message.uuid && playingUuid === message.uuid;
                  return (
                  <div
                    className={
                      "radio-live-item" +
                      (isUnplayed ? " radio-live-item--unplayed" : "") +
                      (isPlayingNow ? " radio-live-item--playing" : "")
                    }
                    key={message.uuid}
                  >
                    <div className="radio-live-item-meta">
                      <strong>
                        {message.senderName}
                        {isUnplayed && !isPlayingNow ? (
                          <span className="radio-live-badge-new">
                            {" "}
                            {isFromWeb ? t("Enviado") : t("Novo")}
                          </span>
                        ) : null}
                      </strong>
                      <span>
                        {message.groupName ? message.groupName + " · " : ""}
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                    {message.audioUrl ? (
                      <audio
                        key={"audio-" + message.uuid}
                        ref={(el) => {
                          if (!message.uuid) return;
                          if (el) {
                            this.audioRefs[message.uuid] = el;
                          } else {
                            delete this.audioRefs[message.uuid];
                          }
                        }}
                        controls
                        preload="metadata"
                        style={{ width: "100%" }}
                        src={this.getPlaybackSrc(message)}
                        onPlay={() => {
                          if (this.programmaticPlay) {
                            this.programmaticPlay = false;
                            return;
                          }
                          const self = this;
                          if (
                            isWebmRadioUrl(message.audioUrl) &&
                            !this.audioBlobUrls[message.uuid]
                          ) {
                            const el = this.audioRefs[message.uuid];
                            if (el) {
                              try {
                                el.pause();
                              } catch (e) {}
                            }
                            this.ensureAudioBlobSrc(message).then(function (blobSrc) {
                              const target = self.audioRefs[message.uuid];
                              if (!blobSrc || !target) {
                                self.beginSequentialFromUser(message);
                                return;
                              }
                              target.src = blobSrc;
                              try {
                                target.load();
                              } catch (e) {}
                              target.play().catch(function () {
                                toast.warn(
                                  t("Não foi possível reproduzir este áudio.")
                                );
                              });
                              self.beginSequentialFromUser(message);
                            });
                            return;
                          }
                          this.beginSequentialFromUser(message);
                        }}
                        onPause={() => {
                          if (this.programmaticPause || this.programmaticPlay) {
                            return;
                          }
                          const el = this.audioRefs[message.uuid];
                          if (!el || el.ended || this.isNearEnd(el)) {
                            return;
                          }
                          if (playingUuid !== message.uuid) {
                            return;
                          }
                          this.stopSequentialPlayback(true);
                        }}
                        onEnded={() => this.onListAudioEnded(message)}
                        onError={() => {
                          const self = this;
                          if (isWebmRadioUrl(message.audioUrl)) {
                            this.ensureAudioBlobSrc(message).then(function (blobSrc) {
                              const el = self.audioRefs[message.uuid];
                              if (blobSrc && el && el.src !== blobSrc) {
                                el.src = blobSrc;
                                try {
                                  el.load();
                                } catch (e) {}
                                el.play().catch(function () {
                                  self.playViaHiddenVideo(
                                    normalizeRadioAudioUrl(message.audioUrl),
                                    message,
                                    function (ok) {
                                      if (!ok) {
                                        toast.warn(
                                          t("Não foi possível reproduzir este áudio.")
                                        );
                                        self.skipFailedAndContinue(message);
                                      }
                                    }
                                  );
                                });
                                return;
                              }
                              self.playViaHiddenVideo(
                                normalizeRadioAudioUrl(message.audioUrl),
                                message,
                                function (ok) {
                                  if (!ok) {
                                    toast.warn(
                                      t("Não foi possível reproduzir este áudio.")
                                    );
                                    self.skipFailedAndContinue(message);
                                  }
                                }
                              );
                            });
                            return;
                          }
                          toast.warn(
                            t("Não foi possível reproduzir este áudio.")
                          );
                          this.skipFailedAndContinue(message);
                        }}
                      >
                        {!this.audioBlobUrls[message.uuid] ? (
                          <source
                            src={normalizeRadioAudioUrl(message.audioUrl)}
                            type={radioAudioMimeType(message.audioUrl)}
                          />
                        ) : null}
                      </audio>
                    ) : (
                      <span className="radio-live-empty">
                        {t("Áudio indisponível")}
                      </span>
                    )}
                  </div>
                  );
                })
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default withTranslation()(RadioLivePanel);
